// Thin wrapper over the Android WebView's `window.VultureStorage` bridge.
// See android/app/src/main/java/com/aduotek/mirroragent/StorageBridge.kt.
//
// In ordinary web (regular Chrome / Safari tab) `window.VultureStorage`
// is undefined, every method is a no-op, and the caller falls back to
// IDB-only behavior — same as before this layer existed.
//
// In the Vulture Chat Android app, every IDB write also lands in a
// native SQLite file inside the app's private storage area. That file
// survives WebView cache evictions, Android's low-storage-pressure
// cache wipes, and Chrome's PWA storage purges — only "clear app data"
// or uninstall takes it out. On app launch we hydrate empty IDB stores
// from SQLite before any hook does its first read, so the user opens
// to lists / calendar / notes immediately instead of an empty shell
// followed by a network round-trip.
//
// All bridge methods are SYNCHRONOUS at the JNI boundary (they cross
// JavascriptInterface), but we wrap them in async so the call site
// stays awaitable and matches localStore's contract. The async wrap
// also gives us a place to hang error logging without throwing — a
// failed bridge call must NEVER block the IDB write that's running
// alongside it.

/* eslint-disable @typescript-eslint/no-explicit-any */
interface NativeStorageBridge {
  set(store: string, key: string, value: string): string;
  get(store: string, key: string): string | null;
  getAll(store: string): string;
  delete(store: string, key: string): string;
  clearStore(store: string): string;
  hasAny(store: string): string;
  storesWithData(): string;
}

function nativeBridge(): NativeStorageBridge | null {
  const w = typeof window !== "undefined" ? (window as any) : null;
  const b = w?.VultureStorage;
  return b && typeof b.set === "function" ? (b as NativeStorageBridge) : null;
}

export function isNativeStorageAvailable(): boolean {
  return nativeBridge() !== null;
}

// Async wrapper — JSON-stringifies on write, parses on read. The bridge
// only knows opaque strings; this layer owns serialization so the
// localStore call site stays typed.
export const nativeStore = {
  available: isNativeStorageAvailable,

  async set(store: string, key: string, value: unknown): Promise<void> {
    const b = nativeBridge();
    if (!b) return;
    try {
      const result = b.set(store, key, JSON.stringify(value));
      if (result !== "ok") console.warn("[native-store] set returned:", result);
    } catch (err: any) {
      console.warn(`[native-store] set(${store}, ${key}) threw:`, err?.message || err);
    }
  },

  async get<T = unknown>(store: string, key: string): Promise<T | undefined> {
    const b = nativeBridge();
    if (!b) return undefined;
    try {
      const raw = b.get(store, key);
      return raw == null ? undefined : (JSON.parse(raw) as T);
    } catch (err: any) {
      console.warn(`[native-store] get(${store}, ${key}) failed:`, err?.message || err);
      return undefined;
    }
  },

  async getAll<T = unknown>(store: string): Promise<Array<{ key: string; value: T; updatedAt: number }>> {
    const b = nativeBridge();
    if (!b) return [];
    try {
      const raw = b.getAll(store);
      const arr = JSON.parse(raw) as Array<{ key: string; value: string; updatedAt: number }>;
      return arr.map((r) => ({ key: r.key, value: JSON.parse(r.value) as T, updatedAt: r.updatedAt }));
    } catch (err: any) {
      console.warn(`[native-store] getAll(${store}) failed:`, err?.message || err);
      return [];
    }
  },

  async delete(store: string, key: string): Promise<void> {
    const b = nativeBridge();
    if (!b) return;
    try { b.delete(store, key); }
    catch (err: any) { console.warn(`[native-store] delete(${store}, ${key}) failed:`, err?.message || err); }
  },

  async clearStore(store: string): Promise<void> {
    const b = nativeBridge();
    if (!b) return;
    try { b.clearStore(store); }
    catch (err: any) { console.warn(`[native-store] clearStore(${store}) failed:`, err?.message || err); }
  },

  async hasAny(store: string): Promise<boolean> {
    const b = nativeBridge();
    if (!b) return false;
    try { return b.hasAny(store) === "true"; }
    catch { return false; }
  },

  async storesWithData(): Promise<Set<string>> {
    const b = nativeBridge();
    if (!b) return new Set();
    try {
      const raw = b.storesWithData();
      const arr = JSON.parse(raw) as string[];
      return new Set(arr);
    } catch (err: any) {
      console.warn("[native-store] storesWithData failed:", err?.message || err);
      return new Set();
    }
  },
};
